// ReviewQueue — scanned quotes whose status nobody chose, one row each, answered
// in place: Completed / Not completed / Unknown (the scanner's labels, scanTruth).
// 1 / 2 / 3 picks for the current row, ↓ / ↑ move between rows; hovering a row
// shows its ticket. One answer per row — there is no "mark all".
// Behind REVIEW_QUEUE_ON; saves through revivalApi.reviewQuote — OPS's
// POST /revival/quotes/<id>/review/ {work_status}. A 409 (moved past the scan,
// signed or paid) closes the row with the reason; nothing is retried for it.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import revivalApi from "../../api/revivalApi";
import { WORK_STATUS_OPTIONS, quoteStatusForWork, reviewRefusal } from "./scanTruth";
import { addressOf, money, quoteTotal, shortDate, ticketDateOf } from "./revivalData";
import "./revival.css";

const LABEL = Object.fromEntries(WORK_STATUS_OPTIONS.map((o) => [o.value, o.label]));

export default function ReviewQueue({ quotes, onSaved, onOpen }) {
  // The queue keeps the rows it opened with: a row answered stays in place (muted,
  // "Saved") so the cursor never jumps under the reviewer's hand.
  const [ids] = useState(() => quotes.map((q) => String(q.id)));
  const byId = useMemo(() => Object.fromEntries(quotes.map((q) => [String(q.id), q])), [quotes]);
  const [rowsSeen, setRowsSeen] = useState(() => Object.fromEntries(quotes.map((q) => [String(q.id), q])));
  useEffect(() => { setRowsSeen((m) => ({ ...m, ...byId })); }, [byId]);
  const rows = ids.map((id) => rowsSeen[id]).filter(Boolean);

  const [cur, setCur] = useState(0);
  const [hover, setHover] = useState(null);
  const [state, setState] = useState({}); // id -> { saving, chosen, saved, error, closed }
  const rowRefs = useRef({});

  const focusRow = useCallback((i) => {
    const next = Math.max(0, Math.min(rows.length - 1, i));
    setCur(next);
    const el = rowRefs.current[rows[next]?.id];
    el?.focus({ preventScroll: true });
    el?.scrollIntoView?.({ block: "nearest" });
  }, [rows]);

  const pick = useCallback(async (q, workStatus) => {
    const id = String(q.id);
    if (!q || state[id]?.saving || state[id]?.closed) return;
    setState((s) => ({ ...s, [id]: { saving: true, chosen: workStatus } }));
    try {
      const { data } = await revivalApi.reviewQuote(q.id, { work_status: workStatus });
      setState((s) => ({ ...s, [id]: { chosen: workStatus, saved: true } }));
      onSaved?.(q.id, {
        work_status: data?.new?.work_status || workStatus,
        status: data?.new?.status || quoteStatusForWork(workStatus),
      });
    } catch (err) {
      const why = reviewRefusal(err);
      setState((s) => ({ ...s, [id]: { error: why.message, closed: why.final } }));
    }
  }, [state, onSaved]);

  const onKeyDown = (e) => {
    if (e.target.closest("input, textarea, select")) return;
    const q = rows[cur];
    if (["1", "2", "3"].includes(e.key) && q) {
      e.preventDefault();
      pick(q, WORK_STATUS_OPTIONS[Number(e.key) - 1].value);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      focusRow(cur + 1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      focusRow(cur - 1);
    } else if (e.key === "Enter" && q && e.target === rowRefs.current[q.id]) {
      e.preventDefault();
      onOpen?.(q);
    }
  };

  const left = rows.filter((q) => !state[String(q.id)]?.saved && !state[String(q.id)]?.closed).length;
  if (!rows.length) return <p className="rv-note">Every scanned quote has a status. Nothing to review.</p>;

  return (
    <div className="rv-queue" onKeyDown={onKeyDown}>
      <div className="rv-queue-head">
        <b>{left} to review</b>
        <span>Was the work on each ticket done? Nothing is chosen for you.</span>
      </div>
      {rows.map((q, i) => {
        const id = String(q.id);
        const st = state[id] || {};
        const chosen = st.chosen || q.work_status || "";
        const img = ticketImageOf(q);
        return (
          <div
            key={id}
            ref={(el) => { rowRefs.current[q.id] = el; }}
            className={`rv-qrow${i === cur ? " is-current" : ""}${st.saved || st.closed ? " is-done" : ""}`}
            tabIndex={i === cur ? 0 : -1}
            role="group"
            aria-label={`${q.quote_number || "Quote"} ${q.customer_name || ""}`}
            onFocus={() => setCur(i)}
            onClick={() => setCur(i)}
            onMouseEnter={() => setHover(id)}
            onMouseLeave={() => setHover((h) => (h === id ? null : h))}
          >
            <span className="rv-q-no rv-cell-dim">{q.quote_number || "—"}</span>
            <button type="button" className="rv-q-who rv-cell-name rv-cell-clip" onClick={() => onOpen?.(q)}>
              {q.customer_name || "—"}
            </button>
            <span className="rv-q-addr rv-cell-dim rv-cell-clip">{addressOf(q) || "—"}</span>
            <span className="rv-q-date rv-cell-dim">{shortDate(ticketDateOf(q))}</span>
            <span className="rv-q-total">{money(quoteTotal(q))}</span>
            <span className="rv-q-pickcell">
              <span className="rv-qpick" role="radiogroup" aria-label="Status">
                {WORK_STATUS_OPTIONS.map((o, k) => (
                  <button key={o.value} type="button" role="radio" aria-checked={chosen === o.value}
                    disabled={!!st.saving || !!st.closed} onClick={() => { setCur(i); pick(q, o.value); }}>
                    <kbd>{k + 1}</kbd>{o.label}
                  </button>
                ))}
              </span>
              <span className={`rv-qstate${st.error ? " is-bad" : ""}`} role={st.error ? "alert" : undefined}>
                {st.saving ? "Saving…" : st.error ? st.error : st.saved ? `Saved · ${LABEL[st.chosen]}` : ""}
              </span>
            </span>
            {hover === id ? (
              <div className="rv-ticket" aria-hidden="true">
                {img ? <img src={img} alt="" /> : <p>No ticket image was kept for this scan.</p>}
              </div>
            ) : null}
          </div>
        );
      })}
      <div className="rv-queue-keys">1 Completed · 2 Not completed · 3 Unknown · ↓ next · ↑ previous · Enter opens the quote</div>
    </div>
  );
}

const IMAGE = /\.(png|jpe?g|gif|webp|bmp|heic|heif)(\?|$)/i;
export const ticketImageOf = (q) => {
  const url = String(q?.ticket_image_url || q?.scanned_file || "");
  return url && IMAGE.test(url) ? url : "";
};
